import React, { useState, useEffect } from 'react';
import { Eye, Pencil, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'https://quizzler-production.up.railway.app';

export default function Hosts() {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHost, setSelectedHost] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [editForm, setEditForm] = useState({
    name: '',
    tier_id: '',
    status: ''
  });

  useEffect(() => {
    fetchHosts();
    fetchTiers();
  }, []);

  const fetchHosts = async () => {
    try {
      const token = localStorage.getItem('quizzler_admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/hosts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setHosts(data.hosts || []);
      } else {
        console.error('Failed to fetch hosts');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching hosts:', error);
      setLoading(false);
    }
  };

  const fetchTiers = async () => {
    try {
      const token = localStorage.getItem('quizzler_admin_token');
      const response = await fetch(`${BACKEND_URL}/api/subscription-tiers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTiers(data.tiers || []);
      }
    } catch (error) {
      console.error('Error fetching tiers:', error);
    }
  };

  const handleView = (host) => {
    setSelectedHost(host);
    setShowViewModal(true);
  };

  const handleEdit = (host) => {
    setSelectedHost(host);
    setEditForm({
      name: host.name,
      tier_id: host.subscription_tier_id || '',
      status: host.status || 'inactive'
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('quizzler_admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/hosts/${selectedHost.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        alert('Host updated successfully!');
        setShowEditModal(false);
        fetchHosts();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update host');
      }
    } catch (error) {
      console.error('Error updating host:', error);
      alert('Failed to update host');
    }
  };

const handleDelete = async () => {
  if (!window.confirm(`Are you sure you want to delete ${selectedHost.name}? This action cannot be undone.`)) {
    return;
  }
  
  try {
    const token = localStorage.getItem('quizzler_admin_token');
    const response = await fetch(`${BACKEND_URL}/api/admin/hosts/${selectedHost.id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.ok) {
      alert('Host deleted successfully');
      setShowEditModal(false);
      fetchHosts();
    } else {
      const data = await response.json();
      alert(data.error || 'Failed to delete host');
    }
  } catch (error) {
    console.error('Error deleting host:', error);
    alert('Failed to delete host');
  }
};

  const filteredHosts = hosts.filter(host =>
    host.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    host.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-gray-600">Loading hosts...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Host Management</h1>
        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Search hosts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
          />
        </div>
      </div>

      {/* Hosts Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Host</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Subscription</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Usage</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Joined</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredHosts.map((host) => (
              <tr key={host.id} className="hover:bg-gray-50 transition">
                <td className="px-6 py-4">
                  <div>
                    <p className="font-semibold text-gray-900">{host.name}</p>
                    <p className="text-sm text-gray-500">{host.email}</p>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{host.tier}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2 w-24">
                      <div
                        className="bg-slate-600 h-2 rounded-full"
                        style={{ width: `${Math.min((host.games_used / (host.games_limit || 1)) * 100, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-600">{host.games_used || 0}/{host.games_limit || '∞'}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                    host.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {host.status || 'inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {new Date(host.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4">
                  <button 
                    onClick={() => handleView(host)}
                    className="text-slate-600 hover:text-slate-700 font-semibold mr-3"
                  >
                    <Eye size={18} className="inline mr-1" /> View
                  </button>
                  <button 
                    onClick={() => handleEdit(host)}
                    className="text-gray-600 hover:text-gray-700 font-semibold"
                  >
                    <Pencil size={18} className="inline mr-1" /> Edit
                  </button>
                  
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredHosts.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No hosts found
          </div>
        )}
      </div>

      {/* View Modal */}
      {showViewModal && selectedHost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Host Details</h2>
              <button onClick={() => setShowViewModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="text-sm font-semibold text-gray-500">Name</label>
                <p className="text-lg text-gray-900">{selectedHost.name}</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="text-sm font-semibold text-gray-500">Email</label>
                <p className="text-lg text-gray-900">{selectedHost.email}</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="text-sm font-semibold text-gray-500">Subscription Tier</label>
                <p className="text-lg text-gray-900">{selectedHost.tier || 'Free Trial'}</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="text-sm font-semibold text-gray-500">Status</label>
                <p className={`text-lg font-semibold ${selectedHost.status === 'active' ? 'text-green-600' : 'text-red-600'}`}>
                  {selectedHost.status || 'inactive'}
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="text-sm font-semibold text-gray-500">Games Used</label>
                <p className="text-lg text-gray-900">{selectedHost.games_used || 0} / {selectedHost.games_limit || '∞'}</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="text-sm font-semibold text-gray-500">Venues</label>
                <p className="text-lg text-gray-900">{selectedHost.venue_count || 0}</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="text-sm font-semibold text-gray-500">Total Games Hosted</label>
                <p className="text-lg text-gray-900">{selectedHost.total_games || 0}</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="text-sm font-semibold text-gray-500">Joined</label>
                <p className="text-lg text-gray-900">{new Date(selectedHost.created_at).toLocaleDateString()}</p>
              </div>
              
              {selectedHost.coupon_code && (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <label className="text-sm font-semibold text-yellow-700">Coupon Code</label>
                  <p className="text-lg text-yellow-800">{selectedHost.coupon_code}</p>
                </div>
              )}
            </div>
            
            <button
              onClick={() => setShowViewModal(false)}
              className="w-full mt-6 bg-slate-600 text-white py-3 rounded-lg hover:bg-slate-700 transition font-semibold"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedHost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-8 max-w-lg w-full">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Edit Host</h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={selectedHost.email}
                  disabled
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-100 text-gray-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Subscription Tier</label>
                <select
                  value={editForm.tier_id}
                  onChange={(e) => setEditForm({ ...editForm, tier_id: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                >
                  <option value="">Free Trial</option>
                  {tiers.map(tier => (
                    <option key={tier.id} value={tier.id}>{tier.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
  <button
    onClick={handleDelete}
    className="bg-red-500 text-white py-3 px-4 rounded-lg hover:bg-red-600 transition font-semibold"
  >
    Delete
  </button>
  <button
    onClick={() => setShowEditModal(false)}
    className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition font-semibold"
  >
    Cancel
  </button>
  <button
    onClick={handleSaveEdit}
    className="flex-1 bg-slate-600 text-white py-3 rounded-lg hover:bg-slate-700 transition font-semibold"
  >
    Save Changes
  </button>
</div>
          </div>
        </div>
      )}
    </div>
  );
}